import { canonicalOr } from "../valuation";
import { Holding, HoldingsSummary } from "./types";

/** Tolerance lives here alone: `Holding` keeps every canonical key required-but-nullable, so a response that drops one is still a compile error below `src/services/`. */
export interface HoldingWire extends Omit<
  Holding,
  | "remainingCostBasis"
  | "positionValue"
  | "unrealizedReturnValue"
  | "unrealizedReturnPercent"
  | "quoteFetchedAt"
> {
  remainingCostBasis?: number | null;
  costBasis?: number | null;
  positionValue?: number | null;
  currentValue?: number | null;
  unrealizedReturnValue?: number | null;
  returnValue?: number | null;
  unrealizedReturnPercent?: number | null;
  returnPercent?: number | null;
  quoteFetchedAt?: string | null;
  currentQuoteFetchedAt?: string | null;
  /** Dropped rather than adopted: nothing reads it, and `providerMarketDate` is the same value. */
  priceDate?: string | null;
}

export const toHolding = (wire: HoldingWire): Holding => {
  // Destructured away, not just left unread: a legacy alias that survived here could still be reached with a cast.
  const {
    costBasis,
    currentValue,
    returnValue,
    returnPercent,
    currentQuoteFetchedAt,
    priceDate,
    ...row
  } = wire;
  return {
    ...row,
    remainingCostBasis: canonicalOr(wire, "remainingCostBasis", "costBasis"),
    positionValue: canonicalOr(wire, "positionValue", "currentValue"),
    unrealizedReturnValue: canonicalOr(
      wire,
      "unrealizedReturnValue",
      "returnValue",
    ),
    unrealizedReturnPercent: canonicalOr(
      wire,
      "unrealizedReturnPercent",
      "returnPercent",
    ),
    quoteFetchedAt: canonicalOr(
      wire,
      "quoteFetchedAt",
      "currentQuoteFetchedAt",
    ),
  };
};

export interface HoldingsSummaryWire extends Omit<
  HoldingsSummary,
  "brokerValue" | "netGain"
> {
  brokerValue?: number | null;
  totalValue?: number | null;
  netGain?: number | null;
  earnedValue?: number | null;
}

export const toHoldingsSummary = (
  wire: HoldingsSummaryWire,
): HoldingsSummary => ({
  accountId: wire.accountId,
  brokerValue: canonicalOr(wire, "brokerValue", "totalValue"),
  netGain: canonicalOr(wire, "netGain", "earnedValue"),
  cashBalance: wire.cashBalance,
  securitiesValue: wire.securitiesValue,
  knownSecuritiesValue: wire.knownSecuritiesValue,
  pricedPositionCount: wire.pricedPositionCount,
  openPositionCount: wire.openPositionCount,
  quoteFetchedAt: wire.quoteFetchedAt,
  valuationState: wire.valuationState,
  valuationStateReason: wire.valuationStateReason,
  valuationAsOf: wire.valuationAsOf,
  realizedReturnValue: wire.realizedReturnValue,
  realizedReturnStateReason: wire.realizedReturnStateReason,
  unrealizedReturnValue: wire.unrealizedReturnValue,
  unrealizedReturnStateReason: wire.unrealizedReturnStateReason,
  withholdingTaxCosts: wire.withholdingTaxCosts,
  totalIncome: wire.totalIncome,
  netIncome: wire.netIncome,
  interestTaxWithheld: wire.interestTaxWithheld,
  brokerCosts: wire.brokerCosts,
});
